import React, { useEffect, useState } from "react";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaKey,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import api from "../../api";

import "../Login.css";

export default function Login() {
  // =========================================================
  // LOGIN STATES
  // =========================================================
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");

  // =========================================================
  // OTP STATES
  // =========================================================
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState(false);

  // =========================================================
  // UI STATES
  // =========================================================
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // =========================================================
  // RESEND OTP
  // =========================================================
  const [resendTimer, setResendTimer] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);

  const navigate = useNavigate();

  // =========================================================
  // PASSWORD SHOW / HIDE
  // =========================================================
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // =========================================================
  // OTP COUNTDOWN
  // =========================================================
  useEffect(() => {
    if (resendTimer <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  // =========================================================
  // START OTP TIMER
  // =========================================================
  const startOtpTimer = () => {
    setResendTimer(60);
  };

  // =========================================================
  // SAVE LOGIN DATA
  // =========================================================
  const saveLoginData = async (userData) => {
    localStorage.setItem("token", userData.token);
    localStorage.setItem("id", userData.id);
    localStorage.setItem("name", userData.name || "");
    localStorage.setItem(
      "role",
      userData.role || "super_admin"
    );
    localStorage.setItem("userType", "platform");

    if (userData.phone) {
      localStorage.setItem("phone", userData.phone);
    }

    // Clear school-related storage
    localStorage.removeItem("school_id");
    localStorage.removeItem("schools");

    await Swal.fire({
      icon: "success",
      title: "Login Successful!",
      text: `Welcome, ${userData.name || "Super Admin"}`,
      timer: 1500,
      showConfirmButton: false,
    });

    navigate("/super_admin/dashboard");
  };

  // =========================================================
  // LOGIN / SEND OTP
  // =========================================================
  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");

    // =======================================================
    // IF OTP STEP IS ACTIVE
    // =======================================================
    if (otpStep) {
      await verifyOtp();
      return;
    }

    // =======================================================
    // MOBILE VALIDATION
    // =======================================================
    if (!/^\d{10}$/.test(mobile)) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }

    // =======================================================
    // PASSWORD VALIDATION
    // =======================================================
    if (!password) {
      setError("Password is required.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    try {
      // =====================================================
      // SEND MOBILE + PASSWORD
      // =====================================================
      const res = await api.post("/super_admin/login", {
        phone: mobile,
        password: password,
      });

      const data = res.data;

      console.log("Super Admin Login Response:", data);

      // =====================================================
      // OTP SENT
      // =====================================================
      if (
        data.status === true &&
        (
          res.status === 202 ||
          data.otp_required === true ||
          data.message?.toLowerCase().includes("otp")
        )
      ) {
        setOtpStep(true);
        setOtp("");
        startOtpTimer();

        await Swal.fire({
          icon: "success",
          title: "OTP Sent",
          text: "A verification OTP has been sent to your registered mobile number.",
          timer: 1800,
          showConfirmButton: false,
        });

        return;
      }

      // =====================================================
      // DIRECT LOGIN FALLBACK
      // =====================================================
      if (data.status === true && data.data?.token) {
        await saveLoginData(data.data);
        return;
      }

      // =====================================================
      // LOGIN FAILED
      // =====================================================
      setError(
        data.message || "Unable to login. Please try again."
      );
    } catch (err) {
      console.error("Super Admin Login Error:", err);

      const message =
        err?.response?.data?.message ||
        "Unable to send OTP. Please try again.";

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // VERIFY OTP
  // =========================================================
  const verifyOtp = async () => {
    setError("");

    // =======================================================
    // OTP VALIDATION
    // =======================================================
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter a valid 6-digit OTP.");
      return;
    }

    setLoading(true);

    try {
      // =====================================================
      // SEND PHONE + PASSWORD + OTP
      // =====================================================
      const res = await api.post("/super_admin/login", {
        phone: mobile,
        password: password,
        otp: otp,
      });

      const data = res.data;

      console.log("Super Admin OTP Response:", data);

      // =====================================================
      // SUCCESS
      // =====================================================
      if (data.status === true && data.data?.token) {
        await saveLoginData(data.data);
        return;
      }

      // =====================================================
      // OTP ERROR
      // =====================================================
      setError(
        data.message || "Invalid OTP. Please try again."
      );
    } catch (err) {
      console.error(
        "Super Admin OTP Verification Error:",
        err
      );

      const message =
        err?.response?.data?.message ||
        "Invalid OTP. Please try again.";

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // RESEND OTP
  // =========================================================
  const handleResendOtp = async () => {
    if (resendTimer > 0 || resendLoading) {
      return;
    }

    setError("");

    if (!/^\d{10}$/.test(mobile)) {
      setError("Invalid mobile number.");
      return;
    }

    setResendLoading(true);

    try {
      const res = await api.post(
        "/super_admin/resend-otp",
        {
          phone: mobile,
        }
      );

      const data = res.data;

      console.log("Resend OTP Response:", data);

      if (data.status === true) {
        setOtp("");
        startOtpTimer();

        await Swal.fire({
          icon: "success",
          title: "OTP Resent",
          text: "A new OTP has been sent to your registered mobile number.",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        setError(
          data.message || "Unable to resend OTP."
        );
      }
    } catch (err) {
      console.error("Resend OTP Error:", err);

      const message =
        err?.response?.data?.message ||
        "Unable to resend OTP. Please try again.";

      setError(message);
    } finally {
      setResendLoading(false);
    }
  };

  // =========================================================
  // CHANGE MOBILE NUMBER
  // =========================================================
  const changeMobile = () => {
    setOtpStep(false);
    setOtp("");
    setError("");
    setResendTimer(0);
  };

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <div className="login-container">

      <form
        className="login-box"
        onSubmit={handleLogin}
      >

        {/* =================================================
            TITLE
        ================================================= */}
        <h2>
          🛠 Platform Super Admin Login
        </h2>

        <p className="subtitle">
          {otpStep
            ? "Verify OTP to continue"
            : "System Owner Access"}
        </p>

        {/* =================================================
            ERROR MESSAGE
        ================================================= */}
        {error && (
          <p className="error-msg">
            {error}
          </p>
        )}

        {/* =================================================
            STEP 1
            MOBILE + PASSWORD
        ================================================= */}
        {!otpStep && (
          <>
            {/* MOBILE */}
            <div className="input-group">

              <FaUser className="icon" />

              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                placeholder="Mobile Number"
                value={mobile}
                autoComplete="tel"
                disabled={loading}
                onChange={(e) => {
                  const value =
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);

                  setMobile(value);
                  setError("");
                }}
              />

            </div>

            {/* PASSWORD */}
            <div className="input-group">

              <FaLock className="icon" />

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                placeholder="Password"
                value={password}
                autoComplete="current-password"
                disabled={loading}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
              />

              {showPassword ? (
                <FaEyeSlash
                  className="toggle-eye"
                  onClick={
                    togglePasswordVisibility
                  }
                />
              ) : (
                <FaEye
                  className="toggle-eye"
                  onClick={
                    togglePasswordVisibility
                  }
                />
              )}

            </div>

            {/* LOGIN BUTTON */}
            <button
              className="login-btn"
              type="submit"
              disabled={loading}
            >
              {loading
                ? "Sending OTP..."
                : "Login"}
            </button>
          </>
        )}

        {/* =================================================
            STEP 2
            OTP
        ================================================= */}
        {otpStep && (
          <>
            {/* OTP INFORMATION */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "18px",
                lineHeight: "1.6",
                fontSize: "14px",
              }}
            >
              <div>
                OTP sent to your registered
                mobile number
              </div>

              <strong>
                ******{mobile.slice(-4)}
              </strong>
            </div>

            {/* OTP INPUT */}
            <div className="input-group">

              <FaKey className="icon" />

              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="Enter 6-digit OTP"
                value={otp}
                autoComplete="one-time-code"
                disabled={loading}
                onChange={(e) => {
                  const value =
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 6);

                  setOtp(value);
                  setError("");
                }}
              />

            </div>

            {/* VERIFY BUTTON */}
            <button
              className="login-btn"
              type="submit"
              disabled={loading}
            >
              {loading
                ? "Verifying..."
                : "Verify OTP & Login"}
            </button>

            {/* RESEND OTP */}
            <div
              style={{
                textAlign: "center",
                marginTop: "16px",
              }}
            >

              {resendTimer > 0 ? (
                <span
                  style={{
                    fontSize: "14px",
                    color: "#777",
                  }}
                >
                  Resend OTP in{" "}
                  <strong>
                    {resendTimer}s
                  </strong>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendLoading}
                  style={{
                    border: "none",
                    background: "none",
                    cursor: resendLoading
                      ? "not-allowed"
                      : "pointer",
                    padding: 0,
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  {resendLoading
                    ? "Sending..."
                    : "Resend OTP"}
                </button>
              )}

            </div>

            {/* CHANGE MOBILE */}
            <div
              style={{
                textAlign: "center",
                marginTop: "12px",
              }}
            >
              <button
                type="button"
                onClick={changeMobile}
                disabled={loading}
                style={{
                  border: "none",
                  background: "none",
                  cursor: loading
                    ? "not-allowed"
                    : "pointer",
                  padding: 0,
                  fontSize: "13px",
                }}
              >
                ← Change Mobile Number
              </button>
            </div>

          </>
        )}

      </form>
    </div>
  );
}